import classNames from "classnames";

import { HighlightedCodeBody } from "@/shared/components/HighlightedCode";
import type { RoboqlQuery } from "@/shared/domain/query";

import styles from "./RoboqlSnippet.module.css";

export interface RoboqlSnippetProps {
  /** The query to render. Rendered verbatim; never parsed for meaning. */
  query: RoboqlQuery;
  /** "chip" renders the first line only, on one line, ellipsised at the
   *  container's max-width. "full" renders every line, wrapped. */
  variant: "chip" | "full";
}

/**
 * Syntax-highlighted RoboQL query text, in the two shapes the filter UI needs:
 * a one-line chip label and the full multi-line query.
 *
 * Sets no width of its own — the chip variant ellipsises against whatever
 * width its container gives it.
 */
export function RoboqlSnippet({ query, variant }: RoboqlSnippetProps) {
  const text = variant === "chip" ? query.firstLine : query.toString();

  return (
    <span
      className={classNames(styles.snippet, {
        [styles.chip]: variant === "chip",
        [styles.full]: variant === "full",
      })}
      data-testid="roboql-snippet"
    >
      <HighlightedCodeBody code={text} language="roboql" />
    </span>
  );
}
